import React, { useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { designTokens } from '@/utils/design-tokens'
import { getCalendarDays, getWeekNumber, isSameDay } from './utils'

interface WeekPickerProps {
  selectedStartDate: Date | null
  selectedEndDate: Date | null
  /** 주(월~일) 선택 완료 */
  onSelect: (startDate: Date, endDate: Date) => void
}

/**
 * 주 단위 전용 피커 (selectionMode='week') — 일 단위 달력 대신
 * 월별 주(월~일) 목록을 보여주고 한 번의 클릭으로 주를 선택한다.
 */
const WeekPicker: React.FC<WeekPickerProps> = ({ selectedStartDate, selectedEndDate, onSelect }) => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const base = selectedStartDate ?? new Date()
    return new Date(base.getFullYear(), base.getMonth(), 1)
  })

  // 표시 중인 달과 겹치는 주(월~일) 목록
  const weeks = useMemo(() => {
    const days = getCalendarDays(currentMonth.getFullYear(), currentMonth.getMonth())
    const result: { start: Date; end: Date; weekNo: number }[] = []
    for (let i = 0; i < 6; i++) {
      const weekDays = days.slice(i * 7, i * 7 + 7)
      if (!weekDays.some(d => d.getMonth() === currentMonth.getMonth())) continue
      result.push({ start: weekDays[0], end: weekDays[6], weekNo: getWeekNumber(weekDays[0]) })
    }
    return result
  }, [currentMonth])

  const fmt = (d: Date) => `${d.getMonth() + 1}월 ${d.getDate()}일`

  const isSelectedWeek = (start: Date, end: Date) =>
    !!selectedStartDate && !!selectedEndDate &&
    isSameDay(start, selectedStartDate) && isSameDay(end, selectedEndDate)

  const isCurrentWeek = (start: Date, end: Date) => {
    const today = new Date()
    return today >= start && today <= new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59)
  }

  return (
    <div style={{ padding: '16px', width: '320px', maxWidth: '90vw' }}>
      {/* 헤더: 월 이동 */}
      <div
        className="flex items-center justify-between mb-3"
        style={{ paddingBottom: '12px', borderBottom: `1px solid ${designTokens.colors.gray[1]}` }}
      >
        <button
          onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
          className="flex items-center justify-center w-8 h-8 rounded hover:bg-gray-50 transition-colors"
          style={{ color: designTokens.colors.gray[5] }}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: designTokens.typography.fontSize['body-medium'][0],
            fontWeight: 600,
            color: designTokens.colors.gray[6],
          }}
        >
          {currentMonth.getFullYear()}년 {String(currentMonth.getMonth() + 1).padStart(2, '0')}월
        </span>
        <button
          onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
          className="flex items-center justify-center w-8 h-8 rounded hover:bg-gray-50 transition-colors"
          style={{ color: designTokens.colors.gray[5] }}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* 주 목록 */}
      <div className="flex flex-col gap-1">
        {weeks.map(week => {
          const selected = isSelectedWeek(week.start, week.end)
          return (
            <button
              key={week.start.toISOString()}
              onClick={() => onSelect(week.start, week.end)}
              className="flex items-center justify-between px-3 py-2 rounded transition-colors"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['body-small'][0],
                backgroundColor: selected ? designTokens.colors.primary.DEFAULT : 'transparent',
                color: selected ? designTokens.colors.white : designTokens.colors.gray[6],
                border: !selected && isCurrentWeek(week.start, week.end)
                  ? `1px solid ${designTokens.colors.primary.DEFAULT}`
                  : '1px solid transparent',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                if (!selected) e.currentTarget.style.backgroundColor = designTokens.colors.gray[0]
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = selected
                  ? designTokens.colors.primary.DEFAULT
                  : 'transparent'
              }}
            >
              <span style={{ fontWeight: 600 }}>{week.weekNo}주차</span>
              <span style={{ color: selected ? designTokens.colors.white : designTokens.colors.gray[5] }}>
                {fmt(week.start)} ~ {fmt(week.end)}
              </span>
            </button>
          )
        })}
      </div>

      {/* 안내 */}
      <div
        className="mt-3 pt-3 text-center"
        style={{
          borderTop: `1px solid ${designTokens.colors.gray[1]}`,
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: designTokens.typography.fontSize['body-xsmall'][0],
          color: designTokens.colors.gray[4],
        }}
      >
        조회할 주를 선택해주세요
      </div>
    </div>
  )
}

export default WeekPicker
